import React, { useState } from "react";
import api from "../context/api";
import "../styles/HighWinners.css";

const HIGH_WINNERS_TIMEOUT_MS = Number(process.env.REACT_APP_HIGH_WINNERS_TIMEOUT_MS || 120000);
const PAGE_SIZE = Number(process.env.REACT_APP_HIGH_WINNERS_PAGE_SIZE || 30);

const formatDateForApi = (isoDate) => {
  const [year, month, day] = String(isoDate || "").split("-");
  if (!year || !month || !day) return "";
  return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
};

const getDisplayAmount = (winner) => {
  const amount = Number(winner?.amount ?? winner?.Amount);
  if (Number.isFinite(amount) && amount > 0) {
    return amount;
  }

  return 0;
};

const HighWinners = () => {
  const [selectedDay, setSelectedDay] = useState("SUNDAY ASEDA");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [highWinners, setHighWinners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const dayOptions = [
    "SUNDAY ASEDA",
    "MONDAY SPECIAL",
    "LUCKY TUESDAY",
    "MIDWEEK",
    "FORTUNE THURSDAY",
    "FRIDAY BONANZA",
    "NATIONAL WEEKLY",
    "NLA VAG MONDAY",
    "NLA NOON RUSH MONDAY",
    "HUNTEX MONDAY",
    "NLA VAG TUESDAY",
    "NLA NOON RUSH TUESDAY",
    "HUNTEX AKOMA TUESDAY",
    "NLA VAG WEDNESDAY",
    "NLA NOON RUSH WEDNESDAY",
    "HUNTEX WEDNESDAY",
    "NLA VAG THURSDAY",
    "NLA NOON RUSH THURSDAY",
    "HUNTEX THURSDAY",
    "NLA VAG FRIDAY",
    "NLA NOON RUSH FRIDAY",
    "HUNTEX FRIDAY",
    "NLA VAG SATURDAY",
    "NLA NOON RUSH SATURDAY",
    "HUNTEX SATURDAY",
    "HUNTEX SUNDAY",
  ];

  const claimAndLoadWinners = async (claimLimit) => {
    const formattedDate = formatDateForApi(selectedDate);
    if (!formattedDate) {
      throw new Error("Invalid date selected.");
    }

    const claimResp = await api.post('/highWinners/claim', {
      selectedDate: formattedDate,
      selectedDay,
      limit: claimLimit,
    }, {
      timeout: HIGH_WINNERS_TIMEOUT_MS,
    });

    const payload = claimResp.data || {};
    const data = payload.winners || payload.data || payload.games || [];
    const totalCount = payload.total != null ? payload.total : data.length;

    return { data, totalCount };
  };

  const fetchHighWinners = async () => {
    if (!selectedDay || !selectedDate) {
      setError("Please select both day and date");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, totalCount } = await claimAndLoadWinners(PAGE_SIZE);

      setHighWinners(data);
      setTotal(totalCount);
      setPage(1);
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out while fetching winners. Please try again.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to fetch high winners');
      }
      setHighWinners([]);
      setTotal(0);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  const markAsProcessed = async (ticketIdentifier) => {
    if (!ticketIdentifier) {
      alert('Missing ticket identifier.');
      return;
    }

    setProcessingId(ticketIdentifier);
    try {
      await api.post('/highWinners/markProcessed', { ticketId: ticketIdentifier });
      
      // Remove from list or update status
      setHighWinners(prev => prev.map(winner => 
        (winner.ticketId || winner.legacyTicketId || winner.id) === ticketIdentifier
          ? { ...winner, processed: true }
          : winner
      ));
      
      // If not showing processed, remove from view and adjust total
      setHighWinners(prev => prev.filter(winner => (winner.ticketId || winner.legacyTicketId || winner.id) !== ticketIdentifier));
      setTotal(t => Math.max(0, t - 1));
    } catch (err) {
      alert(`Error marking as processed: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getLimitLabel = (limit) => {
    switch (JSON.stringify(limit)) {
      case JSON.stringify([1, 2]):
        return 'DIRECT 2';
      case JSON.stringify([1, 3]):
        return 'DIRECT 3';
      case JSON.stringify([3, 24]):
        return 'PERM 2';
      case JSON.stringify([4, 17]):
        return 'PERM 3';
      case JSON.stringify([5, 13]):
        return 'PERM 4';
      case JSON.stringify([6, 12]):
        return 'PERM 5';
      case JSON.stringify([1, 1]):
        return 'Banker Against All';
      case JSON.stringify([1, 24]):
        return 'Banker Against';
      default:
        return JSON.stringify(limit) || 'N/A';
    }
  };

  return (
    <div className="high-winners-container">
      {/* Hero Section */}
      <div className="hw-hero">
        <h1 className="hw-title">High Winners</h1>
        <p className="hw-subtitle">View the biggest winners from your lottery draws</p>
      </div>

      {/* Filters Section */}
      <div className="hw-filters-section">
        <div className="hw-filters">
          <div className="hw-filter-group">
            <label htmlFor="day-select" className="hw-label">Select Game</label>
            <select
              id="day-select"
              className="hw-select"
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
            >
              {dayOptions.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          <div className="hw-filter-group">
            <label htmlFor="date-select" className="hw-label">Select Date</label>
            <input
              id="date-select"
              type="date"
              className="hw-select"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>

          <button className="hw-btn hw-btn-primary" onClick={fetchHighWinners} disabled={loading}>
            {loading ? "Loading..." : "Claim My Winners"}
          </button>
        </div>
        

      </div>

      {/* Results Section */}
      <div className="hw-results-section">
        {error && <div className="hw-error">{error}</div>}

        {highWinners.length === 0 && !loading && !error ? (
          <div className="hw-empty-state">
            <p>Select a game/date and click "Claim My Winners" to get your own queue</p>
          </div>
        ) : loading ? (
          <div className="hw-loading">
            <div className="hw-spinner"></div>
            <p>Fetching winners...</p>
          </div>
        ) : (
          <div>
            <div className="hw-results-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="hw-counts">
                Showing {highWinners.length} of {total}
              </div>
              <button
                className="hw-btn hw-btn-secondary"
                onClick={async () => {
                  setLoadingMore(true);
                  try {
                    const nextPage = page + 1;
                    const nextLimit = nextPage * PAGE_SIZE;
                    const { data, totalCount } = await claimAndLoadWinners(nextLimit);

                    setHighWinners(data);
                    setTotal(totalCount);
                    setPage(nextPage);
                  } catch (err) {
                    alert(err.response?.data?.message || err.message || 'Failed to load more');
                  } finally {
                    setLoadingMore(false);
                  }
                }}
                disabled={loadingMore || loading || highWinners.length >= total}
              >
                {loadingMore ? 'Loading...' : `Load ${PAGE_SIZE} more`}
              </button>
            </div>
            <div className="hw-winners-grid">
              {highWinners.map((winner, idx) => (
                <div key={idx} className={`hw-winner-card ${winner.processed ? 'hw-processed' : ''}`}>
                <div className="hw-winner-rank">#{idx + 1}</div>
                {winner.processed && <div className="hw-processed-badge">✓ Processed</div>}
                <div className="hw-winner-body">
                  <h3 className="hw-winner-name">{winner.fullName || "Anonymous"}</h3>
                  <h3 className="hw-winner-name">{winner.phoneNumber || "Anonymous"}</h3>
                  <div className="hw-winner-details">
                    <div className="hw-detail-row">
                      <span className="hw-detail-label">Minimum Win:</span>
                      <span className="hw-detail-value hw-prize">
                        GHC {Number(winner.minimumWin || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="hw-detail-row">
                      <span className="hw-detail-label">Maximum Win:</span>
                      <span className="hw-detail-value hw-prize">
                        GHC {Number(winner.maximumWin || 0).toLocaleString()}
                      </span>
                    </div>
                     <div className="hw-detail-row">
                      <span className="hw-detail-label">Amount:</span>
                      <span className="hw-detail-value hw-prize">
                          GHC {getDisplayAmount(winner).toLocaleString()}
                      </span>
                    </div>
                     <div className="hw-detail-row">
                      <span className="hw-detail-label">Stake:</span>
                      <span className="hw-detail-value hw-prize">
                        GHC {Number(winner.price || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="hw-detail-row">
                      <span className="hw-detail-label">Date:</span>
                      <span className="hw-detail-value">{winner.NextGameDate}</span>
                    </div>
                    <div className="hw-detail-row">
                      <span className="hw-detail-label">Day:</span>
                      <span className="hw-detail-value">{winner.selectedDay}</span>
                    </div>
                    <div className="hw-detail-row">
                      <span className="hw-detail-label">Game Type:</span>
                      <span className="hw-detail-value">{winner.limitLabel}</span>
                    </div>
                  </div>
                  {winner.selectedNumbers && (
                    <div className="hw-numbers-section">
                      <p className="hw-numbers-label">Winning Numbers:</p>
                      <div className="hw-numbers">
                        {winner.selectedNumbers.map((num, i) => (
                          <span key={i} className="hw-number">
                            {num}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {!winner.processed && (
                    <button 
                      className="hw-btn-mark-done"
                      onClick={() => markAsProcessed(winner.ticketId || winner.legacyTicketId || winner.id)}
                      disabled={processingId === (winner.ticketId || winner.legacyTicketId || winner.id)}
                    >
                      {processingId === (winner.ticketId || winner.legacyTicketId || winner.id) ? 'Processing...' : '✓ Mark as Done'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HighWinners;